import { computed, ref, watchEffect } from 'vue';

/**
 * Menu props that name the element assistive technology treats as the combobox.
 *
 * VSelect puts `role="combobox"` on the field wrapper, while the label the field is given names the
 * `<input>` inside it — so the combobox itself is left without a name. Its menu binds activator
 * props straight onto that wrapper, which is the only way to reach it from outside Vuetify.
 */
export const useComboboxName = (name: () => string) => computed(() => ({ activatorProps: { 'aria-label': name() } }));

/**
 * The same name, for a field whose menu takes no props of its own — VDateInput's, where Vuetify
 * binds the calendar menu's `aria-haspopup` and `aria-expanded` onto the field wrapper but leaves
 * it a plain div, which neither attribute is allowed on. Give the field `role="combobox"` and this
 * ref: the role is the one prop that reaches that element, and the name has to be put on it
 * directly, since everything else Vuetify routes to the `<input>` inside.
 */
export const useComboboxFieldName = (name: () => string) => {
  const field = ref<{ $el?: HTMLElement } | null>(null);
  watchEffect(() => {
    field.value?.$el?.querySelector('.v-field')?.setAttribute('aria-label', name());
  });
  return field;
};
